import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  InfoPageService,
  InfoPage,
} from '../../../core/services/info-page/info-page.service';
import { VersionService, VersionInfo } from '../../../core/services/version/version.service';
import { CurrentUserService } from '../../../core/services/auth/current-user.service';
import { ToastService } from '../../../core/services/toast.service';
import { UserRole } from '../../../../enums/roles.enum';
import { NgxEditorModule, Editor, Toolbar } from 'ngx-editor';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SelectComponent } from '../../../shared/components/select/select.component';

@Component({
  selector: 'app-info-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    NgxEditorModule,
    SelectComponent,
  ],
  templateUrl: './info-page.component.html',
  styleUrl: './info-page.component.scss',
})
export class InfoPageComponent implements OnInit, OnDestroy {
  infoPage: InfoPage | null = null;
  allInfoPages: InfoPage[] = [];
  isSystemAdmin: boolean = false;
  isEditMode: boolean = false;
  isLoading: boolean = false;

  latestVersionInfo: VersionInfo | null = null;
  showPdf: boolean = false;
  safePdfUrl: SafeResourceUrl | null = null;

  togglePdf(): void {
    this.showPdf = !this.showPdf;
  }

  editTitle: string = '';
  editContent: string = '';
  editLanguage: string = 'en-US';

  languageOptions = [
    { value: 'en-US', label: 'English' },
    { value: 'nl-NL', label: 'Nederlands' },
    { value: 'tr-TR', label: 'Türkçe' }
  ];

  editor!: Editor;
  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['code', 'blockquote'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['link', 'image'],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  constructor(
    private infoPageService: InfoPageService,
    private currentUserService: CurrentUserService,
    private toastService: ToastService,
    private translate: TranslateService,
    private router: Router,
    private versionService: VersionService,
    private sanitizer: DomSanitizer
  ) { }

  navigateToCreateVersion(): void {
    this.router.navigate(['/infoPage/version-management']);
  }

  getSafePdfUrl(path: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(path);
  }

  ngOnInit(): void {
    this.editor = new Editor();
    this.checkUserRole();
    this.loadInfoPages();

    this.versionService.versionInfo$.subscribe(info => {
      this.latestVersionInfo = info;
      if (info && info.pdfPath) {
        this.safePdfUrl = this.getSafePdfUrl(info.pdfPath);
      } else {
        this.safePdfUrl = null;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.editor) {
      this.editor.destroy();
    }
  }

  checkUserRole(): void {
    const currentUser = this.currentUserService.GetSession();
    this.isSystemAdmin = currentUser?.role?.name === 'System Admin';
  }

  loadInfoPages(): void {
    this.isLoading = true;
    this.infoPageService.getAllInfoPages().subscribe({
      next: (response) => {
        if (
          response &&
          !response.fail &&
          response.data &&
          response.data.infoPages
        ) {
          this.allInfoPages = response.data.infoPages;

          const currentLang =
            this.translate.currentLang || this.translate.defaultLang || 'en-US';
          this.infoPage =
            this.allInfoPages.find((page) => page.language === currentLang) ||
            null;

          if (!this.infoPage && this.allInfoPages.length > 0) {
            this.infoPage = this.allInfoPages[0];
          }
        } else {
          this.allInfoPages = [];
          this.infoPage = null;
        }
        this.isLoading = false;
      },
      error: (err) => {
        if (err.status === 403) {
          this.allInfoPages = [];
          this.infoPage = null;
        }
        this.isLoading = false;
      },
    });
  }

  enterEditMode(): void {
    setTimeout(() => {
      if (this.infoPage) {
        this.editTitle = this.infoPage.title;
        this.editContent = this.infoPage.content;
        this.editLanguage = this.infoPage.language || 'en-US';
      } else {
        this.editTitle = '';
        this.editContent = '';
        this.editLanguage = 'en-US';
      }
      this.isEditMode = true;
    }, 0);
  }

  cancelEdit(): void {
    this.isEditMode = false;
    this.editTitle = '';
    this.editContent = '';
    this.editLanguage = 'en-US';
  }

  onLanguageChange(): void {
    if (!this.isEditMode) {
      return;
    }

    const selectedLanguagePage = this.allInfoPages.find(
      (page) => page.language === this.editLanguage
    );

    if (selectedLanguagePage) {
      this.editTitle = selectedLanguagePage.title;
      this.editContent = selectedLanguagePage.content;
    } else {
      this.editTitle = '';
      this.editContent = '';
    }
  }

  saveInfoPage(): void {
    if (!this.editTitle.trim() || !this.editContent.trim()) {
      this.toastService.showToast(
        this.translate.instant('warningRequiredFieldsDesc'),
        'warning'
      );
      return;
    }

    this.isLoading = true;

    const existingPage = this.allInfoPages.find(
      (page) => page.language === this.editLanguage
    );
    const pageId = existingPage?.id || crypto.randomUUID();

    const updateData = {
      id: pageId,
      title: this.editTitle,
      content: this.editContent,
      language: this.editLanguage,
    };

    this.infoPageService.updateInfoPage(updateData).subscribe({
      next: (response) => {
        if (response && !response.fail) {
          this.toastService.showToast(
            this.translate.instant('successProcessDesc'),
            'success'
          );
          this.isEditMode = false;
          this.loadInfoPages();
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
      },
    });
  }

  getStageLabel(stage: number): string {
    switch (stage) {
      case 0: return 'VersionManagement.Stages.Alpha';
      case 1: return 'VersionManagement.Stages.Beta';
      case 2: return 'VersionManagement.Stages.ReleaseCandidate';
      case 3: return 'VersionManagement.Stages.Release';
      default: return '';
    }
  }
}
